import { chain, get, isEmpty } from "lodash"
import { createSelector } from "reselect"
import { smallNumberFormatter, numberFormatter } from "helpers/formatters/numbers"
import { getSelectedDate } from "./index"
import { getBasketPositions, getRawSmartTradePositions, getBasketPostionsGMV } from "./positions"

const HEADERS = [
  "Identifier",
  "MIC",
  "Bloomberg Ticker",
  "Country",
  "Asset Class",
  "Currency",
  "Industry",
  "Trade Position Size",
  "Trade Equity %",
]

const getInstrumentDescriptors = (state, props) => props.instrumentDescriptors
const getInstrumentPortfolio = (state, props) => props.instrumentPortfolio

const getFileLabel = createSelector(
  [getInstrumentDescriptors, getInstrumentPortfolio],
  (descriptors, portfolio) => {
    if (!isEmpty(portfolio)) { return portfolio.name }
    return get(descriptors, "ticker", "Unknown")
  },
)

export const getFileName = createSelector(
  [getFileLabel, getSelectedDate],
  (label, date) => `Smart Trade For ${label} - Trades ${date}`,
)

const getSmartSecurityCsvRows = createSelector(
  [getRawSmartTradePositions],
  (contributors) => ([
    HEADERS,
    ...chain(contributors)
      .sortBy((position) => get(position, "economicExposure"))
      .map(({ id, bloombergTicker, country, assetClass, classification, currency, dollarValue, economicExposure }) => {
        const { ticker, mic } = id ?? {}
        return [
          ticker,
          mic,
          bloombergTicker,
          country,
          assetClass,
          currency,
          get(classification, "name"),
          numberFormatter(dollarValue),
          smallNumberFormatter(economicExposure),
        ]
      })
      .value(),
  ]),
)

const getSmartPortfolioCsvRows = createSelector(
  [getBasketPositions, getBasketPostionsGMV],
  (basketPositions, basketPositionsGMV) => ([
    HEADERS,
    ...chain(basketPositions)
      .sortBy(["id"])
      .map(({ id, bloombergTicker, country, assetClass, classification, currency, dollarValue }) => {
        const { ticker, mic } = id
        return [
          ticker,
          mic,
          bloombergTicker,
          country,
          assetClass,
          currency,
          get(classification, "name"),
          numberFormatter(dollarValue),
          smallNumberFormatter(dollarValue / basketPositionsGMV * 100),
        ]
      })
      .value(),
  ]),
)

// Delegates to the memoized rows selector for the active mode only — the portfolio branch
// destructures position ids unguarded, so running it in security mode would throw.
export const getCsvRows = (state, props) => getInstrumentPortfolio(state, props)
  ? getSmartPortfolioCsvRows(state, props)
  : getSmartSecurityCsvRows(state, props)
